/**
 * Doomsworn — the Tilt
 *
 * A campaign-scoped needle between Law and Chaos, visible to the whole table.
 * The d20 stays the d20: the Tilt never touches the resolution die, it changes
 * everything around it — DCs for ordered vs improvised action, spawn tables,
 * weather, faction nerve, Pact Reckonings (±2), and Cast reliability.
 *
 * Runes are immune. Always. That is the strategic hinge of the setting.
 *
 * The Tilt is campaign state owned by the engine (world-engine holds the
 * persistent copy). Everything here is a pure read of a value handed in.
 */
export const TILT_LAW_POLE = -10;
export const TILT_CHAOS_POLE = 10;
/** Inside this band neither side is meaningfully ascendant. */
export const TILT_NEUTRAL_BAND = 3;
export function clampTilt(tilt) {
    return Math.max(TILT_LAW_POLE, Math.min(TILT_CHAOS_POLE, tilt));
}
/** Which side is currently ascendant, or null inside the neutral band. */
export function ascendantSide(tilt) {
    if (tilt <= -TILT_NEUTRAL_BAND)
        return 'law';
    if (tilt >= TILT_NEUTRAL_BAND)
        return 'chaos';
    return null;
}
/** Table-facing label for the needle. */
export function tiltLabel(tilt) {
    const side = ascendantSide(tilt);
    if (!side)
        return 'The Balance holds — barely';
    const depth = Math.abs(tilt);
    const degree = depth >= 8 ? 'at the pole' : depth >= 5 ? 'ascendant' : 'gaining';
    return `${side === 'law' ? 'Law' : 'Chaos'} ${degree} (${tilt > 0 ? '+' : ''}${tilt})`;
}
/**
 * Reckoning modifier: +2 while your Power's side is ascendant, -2 while it is
 * waning, 0 in the neutral band.
 */
export function tiltPactModifier(tilt, powerSide) {
    const side = ascendantSide(tilt);
    if (!side)
        return 0;
    return side === powerSide ? 2 : -2;
}
/**
 * Cast reliability: better under Law (Cast is formal and written down), worse
 * under Chaos (which objects to things being written down on principle).
 */
export function tiltCastModifier(tilt) {
    const steps = Math.round(-tilt / 5);
    return Math.max(-2, Math.min(2, steps));
}
/** Runes do not care about the axis and never have. */
export function tiltRuneModifier(_tilt) {
    return 0;
}
/**
 * Every Draw moves the needle toward the Power's side, by Tier. There is no
 * politically neutral act of sorcery in this setting.
 */
export function applyDrawToTilt(tilt, powerSide, tier) {
    const delta = powerSide === 'chaos' ? tier : -tier;
    return clampTilt(tilt + delta);
}
//# sourceMappingURL=tilt.js.map